import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HiOutlineBell, 
  HiOutlineUserAdd, 
  HiOutlineMail,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineTrash,
  HiOutlineInbox,
  HiOutlineSparkles,
  HiOutlineClock
} from "react-icons/hi";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = Cookies.get("userToken");
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/notification/getNotifications`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNotifications(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const accept = async (id) => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/apis/user/notification/accept/${id}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchNotifications();
  };

  const reject = async (id) => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/apis/user/notification/reject/${id}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchNotifications();
  };

  const deleteOne = async (id) => {
    await axios.delete(
      `${process.env.REACT_APP_BACKEND_URL}/apis/user/notification/delete/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchNotifications();
  };

  const deleteAll = async () => {
    await axios.delete(
      `${process.env.REACT_APP_BACKEND_URL}/apis/user/notification/deleteAll`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotifications([]);
  };

  const openChat = (senderId) => {
    navigate(`/chat/${senderId}`);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'connection_request':
        return <HiOutlineUserAdd className="w-5 h-5" />;
      case 'message':
        return <HiOutlineMail className="w-5 h-5" />;
      default:
        return <HiOutlineBell className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'connection_request':
        return 'from-gray-200 to-gray-300';
      case 'message':
        return 'from-gray-200 to-gray-300';
      default:
        return 'from-gray-200 to-gray-300';
    }
  };

  const SkeletonLoader = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full animate-pulse" />
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
              <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Animated Background Elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        
        {/* Floating Icons - Light Gray */}
        <motion.div 
          className="absolute top-1/4 left-1/4 text-gray-200/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineBell size={80} />
        </motion.div>
      </div>

      {/* Hero Section - Light Gray */}
      <div className="relative w-full h-[30vh] sm:h-[35vh] overflow-hidden">
        <motion.div
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100"
        >
          <div className="absolute inset-0 bg-white/10" />
        </motion.div>
        
        {/* Floating Elements - Light Gray */}
        <motion.div 
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={70} />
        </motion.div>

        {/* Hero Content - Dark text for contrast */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 text-gray-800 drop-shadow-md"
          >
            Notifications
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl px-4"
          >
            Stay updated with your latest activities and requests
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6 md:p-8 border border-gray-200"
        >
          {/* Header - Light Gray */}
          <motion.div 
            variants={itemVariants}
            className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200">
                <HiOutlineBell className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-700">
                  Notifications
                </h2>
                <p className="text-sm text-gray-500">
                  You have {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            {notifications.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={deleteAll}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition border border-red-200"
              >
                <HiOutlineTrash className="w-4 h-4" />
                <span>Clear All</span>
              </motion.button>
            )}
          </motion.div>

          {/* Notifications List */}
          {loading ? (
            <SkeletonLoader />
          ) : notifications.length === 0 ? (
            <motion.div 
              variants={itemVariants}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6 border border-gray-200">
                <HiOutlineInbox className="w-12 h-12 text-gray-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Notifications</h3>
              <p className="text-gray-500">You're all caught up!</p>
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="space-y-4">
                {notifications.map((notification, index) => (
                  <motion.div
                    key={notification._id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, x: -100 }}
                    layout
                    className={`relative bg-white rounded-2xl p-5 shadow-md hover:shadow-lg transition-all duration-300 border ${
                      notification.type === 'connection_request' && notification.status === 'pending'
                        ? 'border-l-4 border-l-gray-400 border-gray-200'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* Avatar Section */}
                      <div className="relative">
                        <img
                          src={
                            notification.sender?.profileImage ||
                            `https://ui-avatars.com/api/?name=${notification.sender?.name || 'User'}&background=6b7280&color=fff&size=100`
                          }
                          alt="profile"
                          className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-200"
                        />
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs border border-gray-300`}>
                          {getNotificationIcon(notification.type)}
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <strong className="text-gray-800 text-lg">
                            {notification.sender?.name || 'Unknown User'}
                          </strong>
                          <div className="flex items-center text-xs text-gray-500">
                            <HiOutlineClock className="w-3 h-3 mr-1" />
                            {formatTime(notification.createdAt)}
                          </div>
                        </div>

                        {notification.type === "connection_request" && (
                          <p className="text-gray-600 mb-2">
                            {notification.sender?.email} sent you a connection request
                          </p>
                        )}

                        {notification.type === "message" && (
                          <p
                            onClick={() => openChat(notification.sender?._id)}
                            className="text-gray-600 hover:text-gray-800 cursor-pointer mb-2 inline-flex items-center space-x-2"
                          >
                            <HiOutlineMail className="w-4 h-4" />
                            <span>New message received. Click to open chat →</span>
                          </p>
                        )}

                        {/* Action Buttons - Light Gray */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          {notification.type === "connection_request" &&
                            notification.status === "pending" && (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-4 py-2 bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition flex items-center space-x-2 shadow-sm"
                                  onClick={() => accept(notification._id)}
                                >
                                  <HiOutlineCheck className="w-4 h-4" />
                                  <span>Accept</span>
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition flex items-center space-x-2"
                                  onClick={() => reject(notification._id)}
                                >
                                  <HiOutlineX className="w-4 h-4" />
                                  <span>Reject</span>
                                </motion.button>
                              </>
                            )}

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                            onClick={() => deleteOne(notification._id)}
                            title="Delete notification"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge for accepted/rejected requests */}
                    {notification.type === "connection_request" && notification.status !== "pending" && (
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${
                        notification.status === 'accepted' 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-red-100 text-red-700 border border-red-200'
                      }`}>
                        {notification.status === 'accepted' ? 'Accepted' : 'Rejected'}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default NotificationPage;